/**
 * src/pages/Alertas.jsx
 * Historial de alertas del sistema (tabla alertas_historial, Fase 3 SLA).
 */
import { useState } from 'react'
import { Header } from '../components/layout/Header'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Table } from '../components/ui/Table'
import { Select } from '../components/ui/Input'
import { useAlertas } from '../hooks/useAdminPanel'

const SEV_COLOR = { critico: 'red', alerta: 'yellow', aviso: 'blue', info: 'gray' }

const SEV_OPTS = [
  { value: '', label: 'Todas las severidades' },
  { value: 'critico', label: 'Crítico' },
  { value: 'alerta', label: 'Alerta' },
  { value: 'aviso', label: 'Aviso' },
  { value: 'info', label: 'Info' },
]

const CAT_OPTS = [
  { value: '', label: 'Todas las categorías' },
  { value: 'PIPELINE', label: 'Pipeline' },
  { value: 'SLA', label: 'SLA' },
  { value: 'SALUD', label: 'Salud' },
  { value: 'SEGURIDAD', label: 'Seguridad' },
]

const PAGE = 50

const COLUMNS = [
  {
    key: 'creado_en', label: 'Fecha', mono: true, nowrap: true, width: '130px',
    render: v => v ? new Date(v + 'Z').toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    }) : '—',
  },
  {
    key: 'severidad', label: 'Severidad', width: '100px',
    render: v => <Badge color={SEV_COLOR[v] || 'gray'} dot>{v}</Badge>,
  },
  { key: 'categoria', label: 'Categoría', mono: true, width: '110px' },
  {
    key: 'titulo', label: 'Alerta',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v}</div>
        <div style={{ fontSize: '11px', color: 'var(--color-text-3)' }}>{row.resumen}</div>
      </div>
    ),
  },
  {
    key: 'cliente_id', label: 'Cliente', mono: true, width: '110px',
    render: v => v || <span style={{ color: 'var(--color-text-3)' }}>—</span>,
  },
  {
    key: 'enviada_telegram', label: 'Canales', width: '90px', mono: true,
    render: (v, row) => {
      const canales = [v && 'TG', row.enviada_email && 'email'].filter(Boolean)
      return canales.length
        ? canales.join(' + ')
        : <span style={{ color: 'var(--color-text-3)' }}>ninguno</span>
    },
  },
]

export default function Alertas() {
  const [severidad, setSeveridad] = useState('')
  const [categoria, setCategoria] = useState('')
  const [offset, setOffset] = useState(0)
  const { data, isLoading } = useAlertas({ severidad, categoria, limit: PAGE, offset })

  const total = data?.total ?? 0
  const pagina = Math.floor(offset / PAGE) + 1
  const paginas = Math.max(1, Math.ceil(total / PAGE))

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="Alertas"
        subtitle={`Historial de avisos del sistema · ${total} registradas`}
        action={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Select value={severidad} options={SEV_OPTS}
                    onChange={e => { setSeveridad(e.target.value); setOffset(0) }} />
            <Select value={categoria} options={CAT_OPTS}
                    onChange={e => { setCategoria(e.target.value); setOffset(0) }} />
          </div>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        <Card padding="0">
          <Table
            columns={COLUMNS}
            data={data?.alertas}
            loading={isLoading}
            emptyMessage="Sin alertas registradas (el historial arranca con el deploy de la Fase 3)"
          />
        </Card>

        {paginas > 1 && (
          <div style={{
            marginTop: '12px', display: 'flex', gap: '10px', alignItems: 'center',
            justifyContent: 'flex-end', fontSize: '12px', fontFamily: 'var(--font-mono)',
            color: 'var(--color-text-2)',
          }}>
            <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - PAGE))}
              style={{
                border: '1px solid var(--color-border)', background: 'var(--color-surface)',
                borderRadius: '6px', padding: '3px 9px',
                cursor: offset === 0 ? 'default' : 'pointer',
                color: offset === 0 ? 'var(--color-text-3)' : 'var(--color-text-2)',
              }}>←</button>
            <span>página {pagina} de {paginas}</span>
            <button disabled={offset + PAGE >= total} onClick={() => setOffset(o => o + PAGE)}
              style={{
                border: '1px solid var(--color-border)', background: 'var(--color-surface)',
                borderRadius: '6px', padding: '3px 9px',
                cursor: offset + PAGE >= total ? 'default' : 'pointer',
                color: offset + PAGE >= total ? 'var(--color-text-3)' : 'var(--color-text-2)',
              }}>→</button>
          </div>
        )}
      </div>
    </div>
  )
}
